// Admin "Users" menu (#3365) — paginated cross-company user list with one
// search input the BE ORs across username / first name / last name / email
// (contract: ourteam-NEW/ourteam-backend#4256). Read-only v1.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import AuthLayout from "@container/layout/auth";
import { Card } from "@component/card";
import { Input } from "@component/input";
import { EmptyState } from "@component/empty-state";
import { Pagination } from "@component/pagination";
import { Button } from "@component/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@component/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@component/dropdown-menu";
import { deleteAdminUser, getAdminUsersList, AdminUserItem } from "@service/admin";
import DrawerCandidateProfile from "@container/drawer/drawer-candidate-profile";
import { CvData } from "@type/candidate";
import { useSnackbar } from "@hook/use-snackbar";
import { useUserProfileContext } from "@context/user_profile";
import { parseFormat } from "@util/date";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";
import { BriefcaseIcon, MoreVerticalIcon, SearchIcon, ShieldAlertIcon, Trash2Icon, UserIcon, UserSearchIcon, UsersIcon } from "lucide-react";

const LIMIT = 20;

// SnackbarProvider lives INSIDE AuthLayout, so any useSnackbar caller must
// render beneath the layout — the page shell only mounts the layout; all the
// logic lives in the inner component.
export default function AdminUsersPage() {
    return (
        <AuthLayout>
            <AdminUsersContent />
        </AuthLayout>
    );
}

function AdminUsersContent() {
    const snackbar = useSnackbar();
    const [, currentUserInfo] = useUserProfileContext();
    const [users, setUsers] = useState<AdminUserItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<"forbidden" | "error" | null>(null);
    const [search, setSearch] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    // Monotonic guard: a slow page-1 response must not overwrite a newer query.
    const requestRef = useRef(0);
    // Bumped after a delete so the list silently refetches in place.
    const [reloadKey, setReloadKey] = useState<number>(0);
    // #3372: Job Seeker / HR tabs + candidate drawer state.
    const [roleGroup, setRoleGroup] = useState<"job_seeker" | "hr">("job_seeker");
    const [roleCounts, setRoleCounts] = useState<{ job_seeker?: number; hr?: number } | null>(null);
    // Candidate-mode drawer (Talent Pool pattern): no job/applicant props, so
    // the drawer shows its Job Applications tab and fetches everything itself
    // from the candidate id. Opens synchronously — no board lookups.
    const [drawerCandidate, setDrawerCandidate] = useState<AdminUserItem | null>(null);
    // #3368: type-to-confirm delete dialog state.
    const [deleteTarget, setDeleteTarget] = useState<AdminUserItem | null>(null);
    const [confirmText, setConfirmText] = useState<string>("");
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Debounce typing into the server-side search; a new query restarts at page 1.
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const requestId = ++requestRef.current;
        setIsLoading(true);
        setError(null);
        getAdminUsersList({ page, limit: LIMIT, search: debouncedSearch, role_group: roleGroup })
            .then((result) => {
                if (requestId !== requestRef.current) return;
                setUsers(result.users);
                setTotalPages(result.pagination.total_pages || 0);
                setTotal(result.pagination.total || 0);
                if (result.role_group_counts) setRoleCounts(result.role_group_counts);
            })
            .catch((err) => {
                if (requestId !== requestRef.current) return;
                setUsers([]);
                setTotalPages(0);
                setTotal(0);
                if (axios.isAxiosError(err) && err.response?.status === 403) {
                    setError("forbidden");
                } else {
                    setError("error");
                    catchErrorLog(`[admin-users] list failed: ${err}`);
                }
            })
            .finally(() => {
                if (requestId === requestRef.current) setIsLoading(false);
            });
    }, [page, debouncedSearch, reloadKey, roleGroup]);

    // #3372: open the candidate drawer in the Talent Pool mode — WITHOUT
    // job/applicant props the drawer shows the Job Applications tab and
    // fetches every read itself from the candidate id.
    const openCandidateDrawer = (u: AdminUserItem) => {
        if (!u.candidate_context?.candidate_id) return;
        setDrawerCandidate(u);
    };

    // The exact string the admin must type — username, email when no username.
    const confirmValue = deleteTarget ? (deleteTarget.username || deleteTarget.email || "") : "";
    const confirmMatches = confirmText === confirmValue && confirmValue !== "";

    const openDeleteDialog = (u: AdminUserItem) => {
        setDeleteTarget(u);
        setConfirmText("");
        setDeleteError(null);
    };

    const handleDelete = () => {
        if (!deleteTarget || !confirmMatches || isDeleting) return;
        setIsDeleting(true);
        setDeleteError(null);
        deleteAdminUser(deleteTarget.id)
            .then(() => {
                snackbar.showSuccess("User deleted.");
                setDeleteTarget(null);
                // Keep the search/page context; step back when the page empties.
                if (users.length === 1 && page > 1) setPage(page - 1);
                else setReloadKey((k) => k + 1);
            })
            .catch((err) => {
                // Surface the BE reason verbatim (400 self-delete / 403 / 404) —
                // the dialog stays open for retry.
                const beMessage = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
                catchErrorLog(`[admin-users] delete failed: ${err}`);
                setDeleteError(beMessage || "Couldn't delete the user. Please try again.");
            })
            .finally(() => setIsDeleting(false));
    };

    const fullName = (u: AdminUserItem) =>
        [u.first_name, u.last_name].filter(Boolean).join(" ");

    return (
            // No container/max-width/padding of its own: AuthLayout already
            // supplies `px-4 sm:px-16 mx-auto` + `mt-8`, and every sibling admin
            // page (Workspaces, Hiring Activity, Subscription Packages) renders
            // straight into it. The extra `container mx-auto px-4 py-6 max-w-7xl`
            // that used to be here double-padded the page, capped it at 1280px
            // and re-centred it inside the already-centred layout — so Users sat
            // indented from the rail while its table ran off the right edge.
            <div>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Every user account across all workspaces — search by username, name, or email.
                        </p>
                    </div>
                </div>

                {/* #3372: role-group tabs — search/pagination are per-tab.
                    House table-tab pattern (icon + label + count pill), the same
                    one Workspaces uses: each tab swaps the table under it, so it
                    is a tab, not a pill filter. */}
                <div className="flex border-b border-gray-200 mb-4">
                    {([
                        { key: "job_seeker" as const, label: "Job Seeker", icon: UserSearchIcon, count: roleCounts?.job_seeker },
                        { key: "hr" as const, label: "HR", icon: BriefcaseIcon, count: roleCounts?.hr },
                    ]).map((tab) => {
                        const Icon = tab.icon;
                        const isActive = roleGroup === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => { if (!isActive) { setRoleGroup(tab.key); setPage(1); } }}
                                className={cn(
                                    "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
                                    isActive
                                        ? "border-blue-600 text-blue-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700",
                                )}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {tab.label}
                                {/* Only once the counts have arrived — an empty pill
                                    while they load reads as a real zero. */}
                                {typeof tab.count === "number" && (
                                    <span
                                        className={cn(
                                            "inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold tabular-nums",
                                            isActive ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500",
                                        )}
                                    >
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                    <div className="relative flex-1 min-w-0">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            className="pl-9"
                            placeholder="Search by username, name, or email…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    {!isLoading && !error && (
                        <span className="flex-shrink-0 text-xs text-gray-500">
                            {total} user{total === 1 ? "" : "s"}
                        </span>
                    )}
                </div>

                {error === "forbidden" ? (
                    <EmptyState
                        icon={<ShieldAlertIcon className="h-12 w-12 text-gray-300" />}
                        title="No access"
                        description="Only platform admins can view the users list."
                    />
                ) : error === "error" ? (
                    <EmptyState
                        icon={<UsersIcon className="h-12 w-12 text-gray-300" />}
                        title="Couldn't load users"
                        description="Something went wrong loading the users list. Try again in a moment."
                    />
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-max">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-left">User</th>
                                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-left">Name</th>
                                        {roleGroup === "job_seeker" ? (
                                            <>
                                                <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-left">Job</th>
                                                <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">Flow Progress</th>
                                                <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-left">Current Step</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">Role</th>
                                                <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-left">Company</th>
                                                <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">Last Login</th>
                                            </>
                                        )}
                                        <th className="h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-center">Created</th>
                                        <th className="h-[45px] px-4 py-3 w-px" aria-label="Actions" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {isLoading ? (
                                        [0, 1, 2, 3, 4].map((i) => (
                                            <tr key={i}>
                                                <td className="px-4 py-4" colSpan={7}>
                                                    <div className="h-9 bg-gray-100 rounded animate-pulse" />
                                                </td>
                                            </tr>
                                        ))
                                    ) : users.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-12">
                                                <EmptyState
                                                    icon={<UsersIcon className="h-12 w-12 text-gray-300" />}
                                                    title={debouncedSearch ? "No matching users" : "No users yet"}
                                                    description={debouncedSearch
                                                        ? `Nothing matches "${debouncedSearch}" across usernames, names, or emails.`
                                                        : "User accounts will appear here."}
                                                />
                                            </td>
                                        </tr>
                                    ) : (
                                        users.map((u) => {
                                            const app = u.candidate_context?.latest_application;
                                            const rowClickable = roleGroup === "job_seeker" && !!u.candidate_context?.candidate_id;
                                            return (
                                            <tr
                                                key={u.id}
                                                className={cn("hover:bg-gray-50 transition-colors", rowClickable && "cursor-pointer")}
                                                onClick={rowClickable ? () => openCandidateDrawer(u) : undefined}
                                            >
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                                            <UserIcon className="w-4 h-4 text-blue-600" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                                {u.username || <span className="text-gray-400">—</span>}
                                                            </div>
                                                            <div className="text-xs text-gray-500 mt-0.5 truncate">
                                                                {u.email || "—"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-gray-700">
                                                    {fullName(u) || <span className="text-gray-400">—</span>}
                                                </td>
                                                {roleGroup === "job_seeker" ? (
                                                    <>
                                                        <td className="px-4 py-4 whitespace-nowrap text-left">
                                                            {app?.job_title ? (
                                                                <div className="min-w-0">
                                                                    <div className="text-sm text-gray-700 truncate max-w-[220px]">{app.job_title}</div>
                                                                    {app.company_name && <div className="text-xs text-gray-400 truncate max-w-[220px]">{app.company_name}</div>}
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-center">
                                                            {app?.flow_progress?.total ? (
                                                                <div className="inline-flex items-center gap-2">
                                                                    <div className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                                                                        <div className="h-full bg-blue-600 rounded-full" style={{ width: `${app.flow_progress.percent}%` }} />
                                                                    </div>
                                                                    <span className="text-xs text-gray-600 tabular-nums">
                                                                        {app.flow_progress.completed} / {app.flow_progress.total}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-gray-700">
                                                            {app?.current_step?.label || <span className="text-gray-400">—</span>}
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-4 py-4 whitespace-nowrap text-center">
                                                            {u.role ? (
                                                                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-gray-50 text-gray-600 border-gray-200">
                                                                    {u.role}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-gray-700">
                                                            {u.company_name || <span className="text-gray-400">—</span>}
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                                            {u.last_login_at ? parseFormat(u.last_login_at, "D MMM YYYY, h:mm A") : <span className="text-gray-400">—</span>}
                                                        </td>
                                                    </>
                                                )}
                                                <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                                    {u.created_at ? parseFormat(u.created_at, "D MMM YYYY") : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <button type="button" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Row actions">
                                                                <MoreVerticalIcon className="h-4 w-4" />
                                                            </button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-48">
                                                            {u.id === currentUserInfo?.id ? (
                                                                <DropdownMenuItem disabled title="You can't delete your own account." className="text-gray-400">
                                                                    <Trash2Icon className="mr-2 h-4 w-4" />
                                                                    Delete user
                                                                </DropdownMenuItem>
                                                            ) : (
                                                                <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => openDeleteDialog(u)}>
                                                                    <Trash2Icon className="mr-2 h-4 w-4" />
                                                                    Delete user
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </td>
                                            </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-4">
                            <Pagination
                                isLoading={isLoading}
                                currentPage={page}
                                totalPages={totalPages}
                                onPageChange={setPage}
                            />
                        </div>
                    </Card>
                )}

                {/* #3372: candidate-mode drawer (Talent Pool pattern) — no job/
                    applicant props, so the Job Applications tab shows and the
                    drawer fetches everything itself from the candidate id. */}
                {drawerCandidate?.candidate_context?.candidate_id && (
                    <DrawerCandidateProfile
                        isOpen
                        cvID=""
                        candidate={{} as CvData}
                        selectedActiveTab="job-applications"
                        user={{
                            id: drawerCandidate.candidate_context.candidate_id,
                            first_name: drawerCandidate.first_name || drawerCandidate.username || "",
                            last_name: drawerCandidate.last_name || "",
                            email: drawerCandidate.email || "",
                            username: drawerCandidate.username || "",
                            profile_picture_path: null,
                            applied_to_job_id: [],
                            cv_id: "",
                            country_code: "",
                            phone_number: "",
                            verification_status: "",
                            role: drawerCandidate.role || "",
                            created_at: drawerCandidate.created_at || "",
                        }}
                        onClose={() => setDrawerCandidate(null)}
                        onReload={() => setReloadKey((k) => k + 1)}
                    />
                )}

                {/* #3368: type-to-confirm delete (BE ourteam-backend#4259). */}
                <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open && !isDeleting) setDeleteTarget(null); }}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Delete {deleteTarget?.username || deleteTarget?.email || "user"}?</DialogTitle>
                            <DialogDescription>
                                This removes the account{deleteTarget?.email ? <> for <b>{deleteTarget.email}</b></> : null} and cannot be undone from here. To confirm, type <b>{confirmValue}</b> below.
                            </DialogDescription>
                        </DialogHeader>
                        <Input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleDelete(); }}
                            placeholder={confirmValue}
                            disabled={isDeleting}
                            autoFocus
                        />
                        {deleteError && (
                            <p className="text-xs text-rose-600">{deleteError}</p>
                        )}
                        <DialogFooter className="gap-2 sm:gap-2">
                            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>Cancel</Button>
                            <Button
                                className="bg-red-600 hover:bg-red-700 text-white"
                                disabled={!confirmMatches || isDeleting}
                                onClick={handleDelete}
                            >
                                {isDeleting ? "Deleting…" : "Delete user"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
    );
}
